// Monorepo package discovery over a viewer-core VFS (a flat absolute-keyed
// Map<path, content>). Mirrors @archlang/lsp's discoverRoots so the web app can
// enumerate packages client-side from the working set — same rules, same shape
// — without a server round-trip. Server (commit trees) and web (VFS) therefore
// agree on what a package is and which one owns a file.

import { parsePackageManifest } from "@archlang/engine";
import { isMarkdownPath, vfsJoin } from "./vfs.js";

export interface VfsRoot {
  /** Bare path of the root relative to the VFS root. "" = the VFS root. */
  readonly path: string;
  readonly kind: "package" | "space";
  /** Dotted manifest name (`package:` / `name:`). Absent for the implicit root. */
  readonly name?: string;
  /** True for the synthesized anonymous package covering loose root files. */
  readonly implicit?: boolean;
}

export interface VfsDiscovery {
  /** Package roots, repo-root package (path "") first, then by path. */
  readonly packages: VfsRoot[];
  /** Space roots (`name:` manifests) — for UI badges. */
  readonly spaces: VfsRoot[];
}

// `__stdlib__` is the reserved VFS mount for the bundled standard library: a
// set of DEPENDENCY packages, never workspace ones. The LSP twin has always
// pruned it; this side hadn't, so web and server disagreed about what a package
// is — the exact thing this module's header promises they never do.
const PRUNE = new Set(["node_modules", ".git", "dist", "__stdlib__"]);

/** Bare, NORMALIZED form of a VFS key — "/a//b/./c.arch" → "a/b/c.arch".
 *
 *  Every path this module compares must go through here. Normalizing only the
 *  manifest keys (and leaving `.arch` keys raw) meant a "svc//api" store
 *  reported the root as "svc/api" while its own file stayed "svc//api/m.arch":
 *  the prefix match failed, the file counted as loose, and discovery invented a
 *  phantom implicit root that sorts FIRST. Keys reach us unnormalized for real
 *  — `sourceLoader.toVirtualPath` strips only a LEADING "./", and
 *  `useWorkspaceModel.toAbsoluteVfs` only prepends "/". */
function bare(path: string): string {
  return vfsJoin("/", path).replace(/^\/+/, "");
}

/** Bare dir of a VFS key, leading slash or not — "lib/a.arch" → "lib".
 *
 *  Studio keys its working set BARE, and the vfs law throws on a relative path:
 *  a manifest at the root of such a set ("package.archspace") took the whole
 *  board down with "vfs paths must be absolute". `bare` absolutises first, so
 *  both conventions land here and both come out normalized. */
function bareDirOf(key: string): string {
  const b = bare(key);
  const slash = b.lastIndexOf("/");
  return slash < 0 ? "" : b.slice(0, slash);
}

function isPruned(path: string): boolean {
  return bare(path)
    .split("/")
    .some((seg) => PRUNE.has(seg));
}

/**
 * Enumerate every package + space root in the VFS. Loose `.arch` files not
 * covered by a declared package are attributed to an implicit package at the
 * VFS root, so nothing is invisible — matching the server's behavior.
 */
export function discoverRootsFromVfs(vfs: ReadonlyMap<string, string>): VfsDiscovery {
  const packages: VfsRoot[] = [];
  const spaces: VfsRoot[] = [];
  const ownedFiles: string[] = [];

  for (const [key, text] of vfs) {
    if (isPruned(key)) continue;
    const name = key.slice(key.lastIndexOf("/") + 1);
    if (name === "package.archspace") {
      const m = parsePackageManifest(text, key).manifest;
      const path = bareDirOf(key);
      // Two conventions can key ONE dir (Studio writes bare, the model layer
      // prepends "/"), and both now resolve — without a dedupe that lands the
      // same root twice, so consumers list a package twice and the
      // deepest-ancestor tie-break picks arbitrarily.
      if (packages.some((p) => p.path === path) || spaces.some((p) => p.path === path)) continue;
      if (m?.package !== undefined) {
        packages.push({ path, kind: "package", name: m.package.join(".") });
      } else if (m?.spaceName !== undefined) {
        spaces.push({ path, kind: "space", name: m.spaceName.join(".") });
      }
    } else if (name.endsWith(".arch") || isMarkdownPath(name)) {
      // Documents count toward package ownership too. Scanning only `.arch`
      // meant a repo whose sources all live in a declared sub-package minted no
      // implicit root for a top-level README — `ownerOfPath` answered undefined,
      // `useActivePackage` passed rootPath: undefined, and opening the README
      // compiled at "/" where the package boundary hides every module: an empty
      // board. `.md` is a first-class workspace file in every LOADER
      // (`load-package` → model.documents, mcp, git-io, Studio's seed).
      ownedFiles.push(bare(key));
    }
  }

  const rootIsPackage = packages.some((p) => p.path === "");
  if (!rootIsPackage) {
    const declared = packages.map((p) => p.path).filter((p) => p !== "");
    const hasOrphan = ownedFiles.some(
      (f) => !declared.some((d) => f === d || f.startsWith(d + "/"))
    );
    if (hasOrphan) packages.push({ path: "", kind: "package", implicit: true });
  }

  packages.sort((a, b) =>
    a.path === "" ? -1 : b.path === "" ? 1 : a.path.localeCompare(b.path)
  );
  spaces.sort((a, b) => a.path.localeCompare(b.path));

  return { packages, spaces };
}

/**
 * Resolve which package owns a file path: the deepest package root that is an
 * ancestor. Returns undefined only when `packages` is empty.
 */
export function ownerOfPath(filePath: string, packages: readonly VfsRoot[]): VfsRoot | undefined {
  return deepestAncestor(filePath, packages);
}

/**
 * Resolve which SPACE a file belongs to: the deepest space-or-package root that
 * is an ancestor. A package boundary is itself a space (the package's root
 * space), and a `name:` space nested inside narrows it further — so the result
 * is the smallest visibility region containing the file. This is the stable key
 * for per-space view state (e.g. remembered viewport): every root has a unique
 * bare `path`, so `spaceOf(file)?.path` keys one viewport per space.
 *
 * Returns undefined only when discovery found no roots at all.
 */
export function spaceOfPath(filePath: string, discovery: VfsDiscovery): VfsRoot | undefined {
  return deepestAncestor(filePath, [...discovery.packages, ...discovery.spaces]);
}

/** Deepest root whose bare path is an ancestor of (or equals) the file. */
function deepestAncestor(filePath: string, roots: readonly VfsRoot[]): VfsRoot | undefined {
  const f = bare(filePath);
  let best: VfsRoot | undefined;
  for (const root of roots) {
    const p = root.path;
    if (!(p === "" || f === p || f.startsWith(p + "/"))) continue;
    if (best === undefined || p.length > best.path.length) best = root;
  }
  return best;
}
